import React from "react";
import { Eye, UserRound, X, Signature } from "lucide-react";
import { resolveMediaUrl } from "../../../../lib/utils";

export const formatSignatureDateTime = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  const datePart = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  const timePart = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return `${datePart} · ${timePart}`;
};

export const normalizeSignatureUrl = (value) => {
  if (!value) return "";
  return resolveMediaUrl(value);
};

export const normalizeText = (value) =>
  String(value || "").trim().toLowerCase();

export const sameText = (a, b) => normalizeText(a) === normalizeText(b);

export const isContractorInternal = (name, item) => {
  const trimmed = String(name || "").trim();
  if (trimmed && trimmed !== "—" && trimmed !== "-") {
    const lower = trimmed.toLowerCase();
    if (
      lower === "internal" ||
      lower === "internal flow" ||
      lower === "assigned to internal team" ||
      lower.startsWith("arkade") ||
      lower === "adl" ||
      lower === "adl ehs" ||
      lower.includes("arkade developer") ||
      lower.includes("arkade nest")
    ) {
      return true;
    }
    // If name is explicitly an external contractor name, it is NOT internal
    return false;
  }

  // If backend explicitly says contractor is NOT internal or user is EXTERNAL
  if (
    item?.is_contractor_internal === false ||
    item?.created_by_user_type === "EXTERNAL" ||
    item?.contractor_type === "EXTERNAL" ||
    item?.contractor?.is_internal === false ||
    item?.contractor?.type === "EXTERNAL"
  ) {
    return false;
  }

  // If backend explicitly says contractor IS internal or user is INTERNAL
  if (
    item?.is_contractor_internal === true ||
    item?.contractor_type === "INTERNAL" ||
    item?.contractor?.is_internal === true ||
    item?.contractor?.type === "INTERNAL"
  ) {
    return true;
  }

  if (item?.created_by_user_type === "EXTERNAL") return false;
  return true;
};

export const resolveContractorName = (permit) => {
  if (!permit) return "";
  if (typeof permit.contractor_name === "string" && permit.contractor_name.trim()) {
    return permit.contractor_name.trim();
  }
  if (typeof permit.contractor_display_name === "string" && permit.contractor_display_name.trim()) {
    const cdn = permit.contractor_display_name.trim();
    if (!isContractorInternal(cdn, permit)) {
      return cdn;
    }
  }
  if (typeof permit.contractor === "string" && permit.contractor.trim()) {
    return permit.contractor.trim();
  }
  if (permit.contractor && typeof permit.contractor === "object") {
    const objName =
      permit.contractor.name ||
      permit.contractor.contractor_name ||
      permit.contractor.company_name ||
      permit.contractor.agency_name ||
      permit.contractor.label ||
      "";
    if (String(objName).trim()) return String(objName).trim();
  }
  if (permit.selected_contractor) {
    if (typeof permit.selected_contractor === "string" && permit.selected_contractor.trim()) {
      return permit.selected_contractor.trim();
    }
    if (typeof permit.selected_contractor === "object") {
      const scName =
        permit.selected_contractor.name ||
        permit.selected_contractor.contractor_name ||
        permit.selected_contractor.company_name ||
        permit.selected_contractor.label ||
        "";
      if (String(scName).trim()) return String(scName).trim();
    }
  }
  if (permit.contractor_details && typeof permit.contractor_details === "object") {
    const cdName =
      permit.contractor_details.name ||
      permit.contractor_details.contractor_name ||
      permit.contractor_details.company_name ||
      "";
    if (String(cdName).trim()) return String(cdName).trim();
  }
  const formData = permit.form_data || {};
  for (const k of [
    "contractor_name",
    "custom_contractor_name",
    "custom_contractor",
    "manual_contractor_name",
    "name_of_contractor",
    "contractor",
    "selected_contractor",
    "agency_name",
    "vendor_name",
    "contractorName",
    "company_name",
    "contractor_agency",
  ]) {
    const val = formData[k];
    if (typeof val === "string" && val.trim()) {
      return val.trim();
    }
    if (val && typeof val === "object") {
      const innerName =
        val.name ||
        val.contractor_name ||
        val.company_name ||
        val.label ||
        (typeof val.value === "string" && Number.isNaN(Number(val.value)) ? val.value : "");
      if (String(innerName).trim()) return String(innerName).trim();
    }
  }
  return "";
};

export const getMakerPositionLabel = (permit) => {
  if (permit?.created_by_user_type?.toLowerCase() === "internal") {
    return "Maker";
  }
  const contractor = resolveContractorName(permit);
  if (contractor && !isContractorInternal(contractor, permit)) {
    return `${contractor}'s Maker`;
  }
  return "Maker";
};

export const getCompletedSignatures = (permit) => {
  const logs = Array.isArray(permit?.workflow_logs)
    ? permit.workflow_logs
    : [];

  const logSigs = logs.flatMap((log) => {
    const signatures = Array.isArray(log?.signatures)
      ? log.signatures
      : Array.isArray(log?.action_signatures)
      ? log.action_signatures
      : [];

    return signatures
      .filter((sig) => Boolean(sig?.signature_url || sig?.signature || sig?.file_url || sig?.image_url))
      .map((sig) => ({
        ...sig,
        workflow_action: log?.action || sig?.action || "",
        status_from: log?.status_from || "",
        status_to: log?.status_to || "",
        actor_group_id: sig?.actor_group_id ?? log?.actor_group_id,
        actor_group_code: sig?.actor_group_code || log?.actor_group_code || log?.from_group_code || "",
        actor_group_name: sig?.actor_group_name || log?.actor_group_name || log?.from_group_name || "",
        performed_by_name: sig?.actor_name || log?.performed_by_name || "",
        performed_at: sig?.created_at || log?.performed_at || "",
        signed_at_display: sig?.signed_at_display || "",
      }));
  });

  const directSigs = Array.isArray(permit?.action_signatures)
    ? permit.action_signatures
        .filter((sig) => Boolean(sig?.signature_url || sig?.signature || sig?.file_url || sig?.image_url))
        .map((sig) => ({
          ...sig,
          workflow_action: sig?.action || "",
          status_from: sig?.status_from || "",
          status_to: sig?.status_to || "",
          actor_group_id: sig?.actor_group_id,
          actor_group_code: sig?.actor_group_code || "",
          actor_group_name: sig?.actor_group_name || "",
          performed_by_name: sig?.actor_name || sig?.performed_by_name || "",
          performed_at: sig?.created_at || "",
          signed_at_display: sig?.signed_at_display || "",
        }))
    : [];

  const allSigs = [...logSigs];
  directSigs.forEach((ds) => {
    if (!allSigs.some((ls) => ls.id && ds.id && String(ls.id) === String(ds.id))) {
      allSigs.push(ds);
    }
  });

  return allSigs;
};

export const findSignature = (signatures, matcher) => {
  return signatures.find((sig) => matcher(sig)) || null;
};

export const getSigUrl = (sig) =>
  sig?.signature_url || sig?.signature || sig?.file_url || sig?.image_url || "";

export const findMakerSignature = (permit) => {
  const signatures = getCompletedSignatures(permit);
  return findSignature(
    signatures,
    (sig) =>
      sig.signature_box_key === "issue_maker_signature" ||
      sameText(sig.workflow_action, "create") ||
      sameText(sig.workflow_action, "submit")
  );
};

export const buildTrainerSignatureUser = (permit, sheet = {}) => {
  const makerSig = findMakerSignature(permit);

  let name =
    sheet.training_conducted_by_name ||
    makerSig?.performed_by_name ||
    makerSig?.actor_name ||
    permit?.created_by_name ||
    "";

  if (!name || String(name).trim().toLowerCase().startsWith("user ")) {
    name = permit?.created_by_name || "Maker";
  }

  const signatureUrl =
    sheet.training_conducted_by_sign ||
    sheet.training_conducted_by_signature_url ||
    sheet.training_conducted_by_signature ||
    getSigUrl(makerSig) ||
    "";

  return {
    key: "tbt_trainer_signature",
    label: "Training Conducted by / Maker",
    subLabel: getMakerPositionLabel(permit),
    badge: "TRAINING CONDUCTED BY",
    name: name,
    signature: signatureUrl,
    signedAtDisplay: makerSig?.signed_at_display || "",
    signedAt: makerSig?.performed_at || makerSig?.created_at || sheet?.date_of_training || "",
    action: makerSig?.workflow_action || makerSig?.action || "submit",
    groupName: makerSig?.actor_group_name || "",
    rawSig: makerSig,
  };
};

export const SignatureViewModal = ({ open, onClose, user }) => {
  if (!open || !user) return null;

  const signatureUrl = normalizeSignatureUrl(user.signature);

  return (
    <div className="fixed inset-0 z-[260] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h3 className="text-base font-bold text-slate-900">
              {user.label}
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              {user.name}
              {user.subLabel ? (
                <span className="font-medium"> ({user.subLabel})</span>
              ) : null}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900 cursor-pointer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5">
          {signatureUrl ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <img
                src={signatureUrl}
                alt={`${user.label} signature`}
                className="mx-auto max-h-72 w-full object-contain"
              />
            </div>
          ) : (
            <div className="flex min-h-[180px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 text-center">
              <Signature className="mb-2 h-8 w-8 text-slate-400" />
              <p className="text-sm font-semibold text-slate-800">
                No signature available
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Latest signature was not found for this user.
              </p>
            </div>
          )}

          {user.declarationText && (
            <div className="mt-4 rounded-xl border border-orange-200 bg-orange-50/80 p-3.5 text-xs text-slate-700">
              <span className="block font-bold text-orange-950 uppercase tracking-wide mb-1 text-[11px]">
                Declaration / Disclaimer Accepted:
              </span>
              <p className="leading-relaxed font-medium">{user.declarationText}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const PermitSignatureCard = ({ user, onView, className = "" }) => {
  if (!user) return null;

  return (
    <div
      className={`flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${className}`}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-600">
          <UserRound className="h-5 w-5" />
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {user.badge || "SIGNED BY"}
          </p>

          <p className="text-sm font-bold text-slate-900">
            {user.name || user.label}
          </p>

          <p className="mt-0.5 text-xs text-slate-500">
            {user.subLabel || user.label}
          </p>

          {user.signedAtDisplay ? (
            <p className="mt-0.5 text-[11px] text-slate-400">
              Signed on {user.signedAtDisplay}
            </p>
          ) : user.signedAt ? (
            <p className="mt-0.5 text-[11px] text-slate-400">
              Signed on {formatSignatureDateTime(user.signedAt)}
            </p>
          ) : null}
        </div>
      </div>

      {onView && (
        <button
          type="button"
          onClick={() => onView(user)}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100 cursor-pointer transition"
        >
          <Eye className="h-3.5 w-3.5" />
          View
        </button>
      )}
    </div>
  );
};

export default PermitSignatureCard;
